import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { StatisticsPanelContent, detectStatCategory } from './statistics/StatisticsPanelContent';
export const InfoPanel = ({ panels, onClose, projectId, isDemoProject = false }) => {
    const [recentlyOpened, setRecentlyOpened] = useState(new Set());
    const [stableContent, setStableContent] = useState(new Map());
    const panelRefs = useRef(new Map());
    const timersRef = useRef(new Map());
    // Track panels that just opened to prevent immediate backdrop close
    useEffect(() => {
        panels.forEach(panel => {
            if (!panelRefs.current.has(panel.id)) {
                // This is a newly opened panel
                panelRefs.current.set(panel.id, true);
                setRecentlyOpened(prev => new Set([...prev, panel.id]));
                // Clear any existing timer for this panel
                const existingTimer = timersRef.current.get(panel.id);
                if (existingTimer) {
                    clearTimeout(existingTimer);
                }
                // Set a timer to allow backdrop clicks after stabilization
                const timer = setTimeout(() => {
                    setRecentlyOpened(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(panel.id);
                        return newSet;
                    });
                    timersRef.current.delete(panel.id);
                }, 300); // Increased to 300ms to handle loading state changes
                timersRef.current.set(panel.id, timer);
            }
            // Track content stability to prevent flickering during data loading
            const currentContent = stableContent.get(panel.id);
            if (panel.content && panel.content !== currentContent) {
                setStableContent(prev => new Map(prev).set(panel.id, panel.content));
                // Reset the recently opened state when content changes (indicates data loading)
                setRecentlyOpened(prev => new Set([...prev, panel.id]));
                // Clear and reset timer when content updates
                const existingTimer = timersRef.current.get(panel.id);
                if (existingTimer) {
                    clearTimeout(existingTimer);
                }
                const timer = setTimeout(() => {
                    setRecentlyOpened(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(panel.id);
                        return newSet;
                    });
                    timersRef.current.delete(panel.id);
                }, 300);
                timersRef.current.set(panel.id, timer);
            }
        });
        // Clean up closed panels from refs and timers
        const currentPanelIds = new Set(panels.map(p => p.id));
        for (const [panelId] of panelRefs.current) {
            if (!currentPanelIds.has(panelId)) {
                panelRefs.current.delete(panelId);
                stableContent.delete(panelId);
                const timer = timersRef.current.get(panelId);
                if (timer) {
                    clearTimeout(timer);
                    timersRef.current.delete(panelId);
                }
            }
        }
    }, [panels, stableContent]);
    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            for (const timer of timersRef.current.values()) {
                clearTimeout(timer);
            }
        };
    }, []);
    const handleBackdropClick = useCallback((panelId) => {
        // Don't close if this panel was just opened or content is still loading
        if (recentlyOpened.has(panelId)) {
            return;
        }
        onClose(panelId);
    }, [recentlyOpened, onClose]);
    if (panels.length === 0)
        return null;
    return (_jsx("div", { className: "fixed inset-0 z-[70] pointer-events-none", children: panels.map((panel, index) => (_jsxs("div", { className: "absolute inset-0 flex items-center justify-center pointer-events-auto", style: { zIndex: 50 + index }, children: [_jsx("div", { className: "absolute inset-0 bg-black bg-opacity-50", onClick: () => handleBackdropClick(panel.id) }), _jsxs("div", { className: "relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden", style: {
                        backgroundColor: 'var(--sand)',
                        borderColor: 'var(--pewter)',
                        border: '2px solid'
                    }, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex items-center justify-between p-4 border-b", style: {
                                backgroundColor: 'var(--charcoal)',
                                borderColor: 'var(--pewter)'
                            }, children: [_jsx("h2", { className: "text-lg font-semibold truncate", style: { color: 'var(--sand)' }, children: panel.title }), _jsx("button", { onClick: () => onClose(panel.id), className: "p-1 rounded hover:bg-opacity-20 hover:bg-white transition-colors", style: { color: 'var(--sand)' }, children: _jsx(X, { size: 20 }) })] }), _jsx("div", { className: "p-4 overflow-y-auto max-h-[calc(80vh-80px)]", style: {
                                color: 'var(--charcoal)',
                                overflowX: 'hidden',
                                wordWrap: 'break-word'
                            }, children: (() => {
                                const statCategory = detectStatCategory(panel.title);
                                // Terrain statistics panels render from structured data (tables,
                                // dual units, range) rather than the legacy server HTML.
                                if (statCategory && projectId) {
                                    // Mean elevation lives only in the legacy HTML body, so pull it
                                    // out of panel.content and hand it to the structured renderer.
                                    let meanHeightM;
                                    if (statCategory === 'elevation') {
                                        const m = panel.content.match(/Mean Elevation:<\/strong>\s*(-?[\d.]+)\s*m/i);
                                        if (m)
                                            meanHeightM = parseFloat(m[1]);
                                    }
                                    return (_jsx(StatisticsPanelContent, { category: statCategory, projectId: projectId, isDemoProject: isDemoProject, meanHeightM: meanHeightM }));
                                }
                                return (_jsx("div", { dangerouslySetInnerHTML: { __html: panel.content }, className: "info-panel-content text-sm leading-normal", style: {
                                        wordWrap: 'break-word',
                                        overflowWrap: 'anywhere'
                                    } }));
                            })() })] })] }, panel.id))) }));
};
